/**
 * Confluence Engine (§16, §17, §18)
 *
 * CPR Confluence Score (max 100, "technical confluence" — never described
 * as accuracy/probability), VWAP + CPR confluence classification and the
 * composed OVERALL MARKET CONDITION. All inputs are read at a closed bar.
 */

import { CandleData } from "../../../types/chart.types";
import {
  BiasState, ConfluenceScore, CprCore, MarketRegime, OverallCondition,
  PositionType, PriceVsCpr, ScoreComponent, StructureState, VwapState, WidthType,
} from "./types";
import { countChopCrosses } from "./market-context";
import { volumeSma as volumeSmaSafe } from "./breakout";

/** Session-level context shared by every per-bar score evaluation. */
export interface ScoreContext {
  widthType: WidthType;
  position: PositionType;
  vwap: Map<number, number> | null;
  structure: StructureState;   // structure at the bar being scored
  vwapState: "ABOVE" | "BELOW" | "N/A"; // VWAP vs close at that bar
  vsCpr: PriceVsCpr;           // close vs CPR at that bar
}

/**
 * §18 — score toward `direction` (the derived bias):
 *   CPR Structure 20 | CPR Width 15 | CPR Position 15
 *   VWAP 15 | Volume 15 | Market Structure 20   (total 100)
 */
export const confluenceScore = (
  candles: CandleData[],
  idx: number,
  ctx: ScoreContext,
  direction: "BULLISH" | "BEARISH" | "NEUTRAL",
): ConfluenceScore => {
  const bull = direction !== "BEARISH"; // NEUTRAL measures the bullish side
  const components: ScoreComponent[] = [];

  // CPR Structure (20): price vs CPR aligned with the measured side
  let structure = 0;
  if (direction === "NEUTRAL") structure = 8;
  else if (ctx.vsCpr === "ABOVE CPR") structure = bull ? 20 : 0;
  else if (ctx.vsCpr === "BELOW CPR") structure = bull ? 0 : 20;
  else structure = 8;
  components.push({ key: "CPR Structure", score: structure, max: 20 });

  // CPR Width (15): narrow = actionable expansion potential
  const width = ctx.widthType === "NARROW" ? 15 : ctx.widthType === "NORMAL" ? 10 : 5;
  components.push({ key: "CPR Width", score: width, max: 15 });

  // CPR Position (15): vs previous CPR
  let position = 8;
  if (direction !== "NEUTRAL") {
    if (ctx.position === "HIGHER CPR") position = bull ? 15 : 0;
    else if (ctx.position === "LOWER CPR") position = bull ? 0 : 15;
    else position = 8;
  }
  components.push({ key: "CPR Position", score: position, max: 15 });

  // VWAP (15): neutral (7) when unavailable
  let vwap = 7;
  if (ctx.vwapState !== "N/A") {
    if (direction === "NEUTRAL") vwap = 7;
    else if (ctx.vwapState === "ABOVE") vwap = bull ? 15 : 0;
    else vwap = bull ? 0 : 15;
  }
  components.push({ key: "VWAP", score: vwap, max: 15 });

  // Volume (15): participation vs its 20-bar average
  const avg = volumeSmaSafe(candles, idx, 20);
  const vol = candles[idx]?.volume ?? 0;
  let volume = 10;
  if (avg > 0 && idx >= 19) {
    const ratio = vol / avg;
    volume = ratio >= 1.5 ? 15 : ratio >= 1 ? 10 : 5;
  }
  components.push({ key: "Volume", score: volume, max: 15 });

  // Market Structure (20)
  let mkt = 10;
  if (direction !== "NEUTRAL") {
    if (ctx.structure === "BULLISH") mkt = bull ? 20 : 0;
    else if (ctx.structure === "BEARISH") mkt = bull ? 0 : 20;
    else mkt = 10;
  }
  components.push({ key: "Market Structure", score: mkt, max: 20 });

  let total = 0;
  for (const c of components) total += c.score;
  return { total, max: 100, direction, components };
};

/**
 * §16 — CPR + VWAP confluence row.
 * Chop (≥4 crossings of VWAP/CPR edges over the last 10 closed bars)
 * outranks directional confluence.
 */
export const vwapConfluenceText = (
  candles: CandleData[],
  lastClosedIdx: number,
  close: number,
  cpr: CprCore,
  vwap: Map<number, number> | null,
): string | null => {
  if (!vwap) return null;
  const crosses = countChopCrosses(candles, lastClosedIdx, vwap, cpr.top, cpr.bottom, 10);
  if (crosses >= 4) return "LOW CONVICTION / CHOPPY";
  const v = vwap.get(lastClosedIdx);
  if (v == null) return null;
  if (close > v && close > cpr.top) return "BULLISH CPR + VWAP CONFLUENCE";
  if (close < v && close < cpr.bottom) return "BEARISH CPR + VWAP CONFLUENCE";
  return null;
};

/** §17 — composed overall market condition (never claims certainty). */
export const overallMarketCondition = (
  widthType: WidthType,
  bias: BiasState,
  regime: MarketRegime,
): OverallCondition => {
  if (
    widthType === "NARROW" &&
    bias !== "NEUTRAL" &&
    (regime === "TRENDING" || regime === "EXPANSION" || regime === "TRANSITION")
  ) {
    return bias === "BULLISH" ? "POTENTIAL BULLISH EXPANSION" : "POTENTIAL BEARISH EXPANSION";
  }
  if (widthType === "WIDE" && (regime === "RANGING" || regime === "CHOPPY")) {
    return "POTENTIAL RANGE / LOW CONVICTION";
  }
  return "NEUTRAL STRUCTURE";
};
